import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output, signal, untracked } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment, { Moment } from 'moment';
import { SharedModule } from '@shared/shared.module';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import {
  HD_RAVE_NEW_YEAR_GATE,
  HD_RAVE_NEW_YEAR_MAX_YEAR,
  HD_RAVE_NEW_YEAR_MIN_YEAR,
  clampHdRaveNewYear,
} from '../../transits/rave-new-year/hd-rave-new-year';
import {
  HdRaveNewYearRequest,
  HdRaveNewYearService,
  HdRaveNewYearVariant,
} from '../../transits/rave-new-year/hd-rave-new-year.service';
import { HD_JUST_NOW_ICONS } from './hd-chart-cycle-metadata';

const I18N_PREFIX = 'humanDesign.chartManager.raveNewYear';

/** Moment van het gekozen jaar: `undefined` = nog aan het berekenen, `null` = niet te bepalen. */
interface RaveNewYearMomentState {
  readonly year: number;
  readonly moment: Moment | null | undefined;
}

/**
 * Jaarkeuze voor de Rave New Year-chart (snelle actie in de chart manager): het
 * jaar (standaard het Rave-jaar waarin we zitten), het moment waarop de zon
 * poort 41 binnengaat in lokale tijd, en de twee varianten zoals bij Just Now.
 * Kiezen sluit de dialoog; de host opent de chart.
 */
@Component({
  selector: 'hd-rave-new-year-dialog',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './hd-rave-new-year-dialog.component.html',
  styleUrl: './hd-rave-new-year-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdRaveNewYearDialogComponent {
  private readonly raveNewYear = inject(HdRaveNewYearService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly open = model(false);
  readonly requested = output<HdRaveNewYearRequest>();

  protected readonly minYear = HD_RAVE_NEW_YEAR_MIN_YEAR;
  protected readonly maxYear = HD_RAVE_NEW_YEAR_MAX_YEAR;
  protected readonly icons = HD_JUST_NOW_ICONS;

  /** Gekozen jaar; `null` tot het huidige Rave-jaar bekend is. */
  protected readonly year = signal<number | null>(null);
  private readonly momentState = signal<RaveNewYearMomentState | null>(null);
  private openSeq = 0;
  private momentSeq = 0;

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const translate = (key: string, params?: Record<string, unknown>) =>
      this.transloco.translate<string>(`${I18N_PREFIX}.${key}`, params, lang);
    return {
      title: translate('title'),
      intro: translate('intro', { gate: HD_RAVE_NEW_YEAR_GATE }),
      yearLabel: translate('yearLabel'),
      previousYear: translate('previousYear'),
      nextYear: translate('nextYear'),
      momentLabel: translate('momentLabel', { gate: HD_RAVE_NEW_YEAR_GATE }),
      loading: translate('loading'),
      notFound: translate('notFound'),
      full: translate('full'),
      fullTooltip: translate('fullTooltip'),
      transit: translate('transit'),
      transitTooltip: translate('transitTooltip'),
    };
  });

  /** Het moment van het gekozen jaar in lokale tijd, in de content-taal. */
  protected readonly momentText = computed<string | null>(() => {
    const state = this.momentState();
    if (state == null || state.year !== this.year() || state.moment == null) return null;
    const locale = momentLocaleFor(this.contentLang.effectiveLang());
    return moment(state.moment).locale(locale).format('dddd D MMMM YYYY, HH:mm');
  });

  protected readonly momentLoading = computed(() => {
    const state = this.momentState();
    return this.year() == null || state == null || state.year !== this.year() || state.moment === undefined;
  });

  protected readonly momentMissing = computed(() => !this.momentLoading() && this.momentText() == null);

  constructor() {
    // Bij elk openen: het Rave-jaar waarin we nu zitten.
    effect(() => {
      if (!this.open()) return;
      untracked(() => void this.resetToCurrentYear());
    });
    effect(() => {
      const year = this.year();
      if (year != null) untracked(() => void this.loadMoment(year));
    });
  }

  protected onOpenChange(open: boolean): void {
    this.open.set(open);
  }

  protected shiftYear(delta: number): void {
    const year = this.year();
    if (year != null) this.year.set(clampHdRaveNewYear(year + delta));
  }

  /** Getypt jaar: pas toegepast bij `change` (Enter of verlaten), begrensd op het domein. */
  protected onYearInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = Number(target.value);
    if (!Number.isFinite(value) || target.value.trim() === '') {
      target.value = `${this.year() ?? ''}`;
      return;
    }
    const year = clampHdRaveNewYear(value);
    target.value = `${year}`;
    this.year.set(year);
  }

  protected choose(variant: HdRaveNewYearVariant): void {
    const year = this.year();
    if (year == null || this.momentText() == null) return;
    this.requested.emit({ year, variant });
    this.open.set(false);
  }

  private async resetToCurrentYear(): Promise<void> {
    const seq = ++this.openSeq;
    this.year.set(null);
    const year = await this.raveNewYear.currentYear();
    if (seq === this.openSeq) this.year.set(year);
  }

  private async loadMoment(year: number): Promise<void> {
    const seq = ++this.momentSeq;
    this.momentState.set({ year, moment: undefined });
    const found = await this.raveNewYear.momentFor(year);
    if (seq === this.momentSeq) this.momentState.set({ year, moment: found });
  }
}
